import styled from '@emotion/styled';
import Thumbnail from '@base/Thumbnail/Thumbnail';
import Link from '@base/Link/Link';
import Tags from '@base/Tags/Tags';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import getTopicGroupAssetObj from '@cont/Syllabus/constants/getTopicGroupAssetObj';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { getFullName } from '@models/User';
import H6 from '@base/H6/H6';
import P2 from '@base/P2/P2';
import { useIsMobile } from '@stores/AppCommonData';
import Grow from '@base/Grow/Grow';
import getDurationStr from '@utils/getDurationStr';
import { segment } from '@actions/analytics';
import FetchAndRender from '@cont/Syllabus/TopicPage/chapterInfo/FetchAndRender';
import { useState } from 'react';
import RecordedVideoBlockDialog from '@cont/Syllabus/TopicPage/chapterInfo/RecordedVideoBlockDialog';
import useConstants from '@api/hooks/useConstants';
import { getIsRCPlayAllowed } from '@api/selectors/constants';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { Container } from '../CourseCard';
import SvgThumbail from '../PlusCourseCard/placeholderCourseThumbail/SvgThumbnail';
import CoursePlaceholder from '../PlusCourseCard/placeholderCourseThumbail/CoursePlaceholder';

const SvgCourseThumbnail = styled(SvgThumbail)`
  width: 272px;
  height: ${({ $orientation }) =>
    $orientation === 'horizontal' ? '152px' : 'auto'};
  ${mediaQuery.uptoMobile} {
    width: 100%;
    border-radius: 0;
  }
`;

const LessonThumbnail = styled(Thumbnail)`
  width: 272px;
  height: ${({ $orientation }) => $orientation === 'horizontal' && '152px'};
  ${mediaQuery.uptoMobile} {
    width: 100%;
    border-radius: 0;
  }
`;

const LinkContainer = styled.div`
  cursor: pointer;
`;

const CardContainer = styled(Container)`
  display: flex;
  flex: 1;
  overflow: hidden;
  height: 100%;
  flex-direction: ${({ $orientation }) =>
    $orientation === 'horizontal' ? 'row' : 'column'};
  ${mediaQuery.uptoMobile} {
    flex: auto;
    box-shadow: ${boxShadowGenerator({
      blur: Spacings.SPACING_4B
    })};
  }
`;

const PlaceholderThumbnail = styled(CoursePlaceholder)`
  height: 100%;
  width: 100%;
  transform: scale(1.02);
  background-color: var(--color-divider);
  object-fit: cover;
  ${mediaQuery.uptoMobile} {
    transform: scale(1);
  }
`;

const DefaultThumbnail = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: ${Spacings.SPACING_2B};
`;

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  background-color: var(--color-base-1);
  text-align: left;
  padding: ${Spacings.SPACING_4B}
    ${({ $orientation }) =>
      $orientation === 'horizontal'
        ? Spacings.SPACING_6B
        : Spacings.SPACING_4B};
`;

const TopicGroupTag = styled(Tags)`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  padding: 0;
  width: 100%;
  max-width: none;
  color: ${({ $color }) => $color};
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.EXTRA_SMALL};
  }
`;

const BreadCrumbs = styled.div`
  padding-bottom: ${Spacings.SPACING_2B};
  display: grid;
  grid-template-columns: max-content 1fr;
  grid-column-gap: ${Spacings.SPACING_2B};
  align-items: center;
  ${mediaQuery.uptoMobile} {
    padding-bottom: ${Spacings.SPACING_1B};
  }
`;

const CourseTitle = styled(H6)`
  padding-bottom: ${Spacings.SPACING_1B};
  color: var(--color-text-primary);
  cursor: pointer;
  ${mediaQuery.uptoMobile} {
    padding-bottom: 0;
    font-size: ${Fonts.LARGE};
  }
`;

const CourseInfo = styled(P2)`
  padding-bottom: ${Spacings.SPACING_1B};
  ${mediaQuery.uptoMobile} {
    padding-bottom: 0;
    margin-bottom: ${Spacings.SPACING_4B};
  }
`;

const EducatorName = styled(P2)`
  padding-top: ${Spacings.SPACING_2B};
  color: var(--color-text-primary);
`;

const LessonCard = ({
  label: sectionLabel,
  className,
  gridColumn,
  orientation,
  lesson,
  imageBorderRadius,
  dataAnalytics,
  lastPrimarySource,
  goalUID
}) => {
  const { programme, uid, name, videoDuration } = lesson;
  const [openCLXInterface, setOpenCLXInterface] = useState(false);

  const {
    coverPhotoV1,
    coverPhoto,
    topicGroups,
    languageDisplay: language,
    goal,
    author
  } = programme;
  const topic = topicGroups && topicGroups[0];
  const topicName = topic.name;
  const { color } = getTopicGroupAssetObj(topicName);
  const goalSegmentData = useGoalEventProps({ goalUID });
  const isMobile = useIsMobile();
  const [showRecordedGoalDialog, setShowRecordedGoalDialog] = useState(false);
  const isRecordedContentPlayAllowed = useConstants(
    getIsRCPlayAllowed(goalUID)
  );

  const handleCloseRecordedVideoDialog = () => {
    setShowRecordedGoalDialog(false);
  };

  const handleAnalytics = () => {
    segment.track('Lesson Viewed', {
      last_primary_source: lastPrimarySource,
      last_primary_source_section: sectionLabel,
      lesson_uid: uid,
      lesson_title: name,
      educator_uid: author.uid,
      educator_username: author.username,
      ...goalSegmentData
    });
  };

  const onTopicClick = (e) => {
    if (isMobile) {
      e.preventDefault();
    }
  };

  const onLessonClick = () => {
    if (!isRecordedContentPlayAllowed) {
      setShowRecordedGoalDialog(true);
      return;
    }
    handleAnalytics();
    setOpenCLXInterface(true);
  };

  return (
    <>
      <CardContainer
        className={className}
        gridColumn={gridColumn}
        $orientation={orientation}
      >
        <LinkContainer onClick={onLessonClick}>
          {coverPhotoV1 || coverPhoto ? (
            <LessonThumbnail
              imgUrl={coverPhotoV1 || coverPhoto}
              borderRadius={imageBorderRadius}
              data-analytics={`${dataAnalytics}-courseCard`}
            />
          ) : (
            <SvgCourseThumbnail
              as={DefaultThumbnail}
              $orientation={orientation}
            >
              <PlaceholderThumbnail color={color} />
            </SvgCourseThumbnail>
          )}
        </LinkContainer>

        <Wrapper $orientation={orientation}>
          <div>
            <BreadCrumbs>
              {language && <Tags type="hollow">{language}</Tags>}
              {(topic?.uid || topic?.name) && (
                <>
                  <Link
                    href="/goal/[goalSlug]/[goalUID]/topic/[topicUid]"
                    as={`/goal/${goal?.goalSlug || goal?.slug}/${
                      goal?.uid
                    }/topic/${topic.uid}`}
                    anchorStyle="display: inline-block;min-width: 0;line-height: 1;"
                    onClick={onTopicClick}
                  >
                    <TopicGroupTag type="transparent" $color={color}>
                      {topic.name}
                    </TopicGroupTag>
                  </Link>
                </>
              )}
            </BreadCrumbs>

            <CourseTitle lineClamp={2} as="h4" onClick={onLessonClick}>
              {name}
            </CourseTitle>

            <CourseInfo color="var(--color-text-secondary)">
              {getDurationStr(videoDuration, true)}
            </CourseInfo>
          </div>
          <Grow />

          <Link href="/[authorID]" as={`/@${author.username}`}>
            <EducatorName as="h4">
              {getFullName(author.firstName, author.lastName)}
            </EducatorName>
          </Link>
        </Wrapper>
      </CardContainer>

      {openCLXInterface && (
        <FetchAndRender
          uid={uid}
          goalUID={goal.uid}
          closeCLXInterface={setOpenCLXInterface}
          isDemoVideo={false}
          source="syllabus_feed"
        />
      )}

      <RecordedVideoBlockDialog
        isOpen={showRecordedGoalDialog}
        onClose={handleCloseRecordedVideoDialog}
      />
    </>
  );
};

export default LessonCard;
